import {
  Lead,
  GuideSection,
  P,
  OL,
  LI,
  Term,
  GuideLink,
  Callout,
  GuideTable,
} from './guide-ui';

/**
 * Body for "What is risk-based alerting?" guide. Educational-first; the
 * LogPulse-specific model is described in the final section.
 */
export function RiskBasedAlertingGuideBody() {
  return (
    <>
      <Lead>
        Traditional SIEMs fire one alert per rule match, which buries analysts in
        thousands of low-fidelity alerts. Risk-based alerting (RBA) flips the model:
        instead of alerting on every match, it accumulates risk against the entity
        involved and only raises a case when that risk crosses a threshold. The
        result is far fewer, far higher-confidence alerts. This guide explains how
        RBA works and what separates a good implementation from a noisy one.
      </Lead>

      <GuideSection id="the-problem" title="The problem RBA solves: alert fatigue">
        <P>
          A rule-per-alert SIEM treats every detection as independently
          alert-worthy. A single user doing something mildly unusual can trip a
          dozen rules, each firing its own alert, while a real, multi-stage
          intrusion looks the same size as the noise. Analysts drown, tune rules
          down to survive, and miss the signal that mattered. RBA exists to break
          that volume-equals-importance trap.
        </P>
      </GuideSection>

      <GuideSection id="what-is-rba" title="What is risk-based alerting?">
        <P>
          <Term>Risk-based alerting</Term> is an approach where detections do not
          alert directly. Instead, each detection emits a <Term>risk event</Term>{' '}
          attributed to a <Term>risk object</Term>, an entity such as a user, host,
          or IP, and assigns it a risk contribution. Risk events accumulate on the
          entity over time. Only when an entity&apos;s total risk crosses a defined{' '}
          <Term>threshold</Term> is a single <Term>notable</Term> (an investigatable
          case) raised, carrying all the contributing context with it.
        </P>
        <P>
          Each risk event is typically enriched: annotated against a{' '}
          <Term>MITRE ATT&CK</Term> technique, weighted by detection fidelity, and
          adjusted for how critical the asset or identity is. The unit of attention
          shifts from{' '}
          <Term>&quot;which rule fired?&quot;</Term> to{' '}
          <Term>&quot;which entity is behaving like a threat?&quot;</Term>
        </P>
      </GuideSection>

      <GuideSection id="how-it-works" title="How risk-based alerting works">
        <OL>
          <LI><Term>Detections emit risk, not alerts.</Term> A rule match produces a risk event attributed to an entity, with a score and MITRE context.</LI>
          <LI><Term>Risk accumulates on the entity.</Term> Multiple signals across time and data sources add up against the same user, host, or IP.</LI>
          <LI><Term>Context is layered in.</Term> Threat-intel hits, behavioral anomalies (UEBA), and asset criticality adjust the contribution.</LI>
          <LI><Term>A threshold promotes to a notable.</Term> When the entity&apos;s risk crosses the line, one case is raised, with the full trail of contributing events.</LI>
          <LI><Term>Risk decays.</Term> As time passes without new activity, risk falls again, so old noise does not keep an entity permanently hot.</LI>
        </OL>
      </GuideSection>

      <GuideSection id="reduces-fatigue" title="Why RBA reduces alert fatigue">
        <P>
          Because alerts are raised per entity at a threshold rather than per rule
          match, RBA collapses a flood of low-fidelity alerts into a curated set of
          high-impact cases. Industry implementations report cutting low-fidelity
          alert volume by roughly <Term>50–90%</Term>. Just as important, RBA
          surfaces the <Term>breadth</Term> of an attack: an entity that trips many
          weak signals across multiple attack stages rises above one that trips the
          same rule a thousand times.
        </P>
        <Callout title="The design detail that matters">
          <p>
            A naive risk score that simply adds up every event lets sheer volume
            outrank a real intrusion. A good score applies{' '}
            <Term>diminishing returns</Term> so repeats of the same signal saturate,
            and an <Term>attack-breadth boost</Term> so activity spanning more MITRE
            tactics and techniques is weighted higher. Volume should never beat
            breadth.
          </p>
        </Callout>
      </GuideSection>

      <GuideSection id="rba-vs-traditional" title="RBA vs. traditional alerting">
        <GuideTable
          head={['', 'Traditional alerting', 'Risk-based alerting']}
          rows={[
            ['Unit of alerting', 'One alert per rule match', 'Risk attributed to an entity'],
            ['Typical volume', 'Thousands of alerts', 'A handful of high-confidence notables'],
            ['Context', 'Each alert is isolated', 'Signals accumulate with MITRE + threat-intel context'],
            ['Promotion', 'Every match fires', 'One threshold on one score'],
            ['Noise handling', 'Repeats flood the queue', 'Repeats saturate; breadth is rewarded'],
          ]}
        />
      </GuideSection>

      <GuideSection id="how-logpulse-helps" title="How LogPulse does risk-based alerting">
        <P>
          In LogPulse, every signal, detections, behavioral analytics (UEBA), and
          threat intelligence, emits a risk event attributed to an entity. Every
          entity carries one <Term>bounded 0–100 effective risk score</Term>: the
          same number on the leaderboard, on the notable, and in the promotion
          decision. The score uses diminishing returns so repetitive noise
          saturates, an attack-breadth boost so activity across more MITRE stages
          weighs more, time decay, and an entity-criticality multiplier.
        </P>
        <P>
          Promotion is one threshold on one score; there is one open notable per
          entity; and a raised notable auto-resolves once risk decays below a clear
          threshold. Scores map to five bands (Info, Low, Moderate, High, Critical),
          and the scoring is tunable per organisation. See{' '}
          <GuideLink href="/siem">Security Monitoring (SIEM)</GuideLink> for the full
          picture, and{' '}
          <GuideLink href="/guides/what-is-an-agentic-soc">what is an agentic SOC</GuideLink>{' '}
          for how AI then triages those notables with a human in the loop.
        </P>
      </GuideSection>
    </>
  );
}
